import React, { useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert, ArrowDown, ArrowUp, Badge, Breadcrumbs, Button, Card, EmptyState, Table, TextInput,
} from '@opendate-app/setlist';
import CrmPageLayout from '../../shared/components/CrmPageLayout';

function errorMessageFrom(error) {
  const errors = error?.response?.data?.errors;
  return Array.isArray(errors) && errors.length > 0 ? errors.join(', ') : 'Something went wrong. Try again.';
}

export default function CrmContactTypesPage({ team, csrfToken, contactTypes = [], canManage = false }) {
  const [types, setTypes] = useState(contactTypes);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const skipBlurSave = useRef(false);

  const basePath = `/teams/${team.id}/contact_types`;
  const activeTypes = types.filter((type) => !type.archived);

  const run = async (request) => {
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    setBusy(true);
    setError(null);
    try {
      const { data } = await request();
      setTypes(data.contact_types);
      return true;
    } catch (err) {
      setError(errorMessageFrom(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addType = async () => {
    const name = newName.trim();
    if (!name) return;
    const saved = await run(() => axios.post(`${basePath}.json`, { contact_type: { name } }));
    if (saved) setNewName('');
  };

  const startRename = (type) => {
    skipBlurSave.current = false;
    setEditingId(type.id);
    setEditingName(type.name);
  };

  const saveRename = async (type) => {
    const name = editingName.trim();
    if (!name || name === type.name) {
      setEditingId(null);
      return;
    }
    const saved = await run(() => axios.patch(`${basePath}/${type.id}.json`, { contact_type: { name } }));
    if (saved) setEditingId(null);
    else skipBlurSave.current = false;
  };

  const move = (type, position) => run(() => axios.patch(`${basePath}/${type.id}/move.json`, { position }));
  const archive = (type) => run(() => axios.patch(`${basePath}/${type.id}/archive.json`));
  const restore = (type) => run(() => axios.patch(`${basePath}/${type.id}/unarchive.json`));

  const renderName = (type) => {
    if (!canManage || editingId !== type.id) {
      return (
        <span className="d-inline-flex align-items-center">
          <span className={type.archived ? 'text-muted' : undefined}>{type.name}</span>
          {type.archived && <span className="ml-2"><Badge color="gray">Archived</Badge></span>}
        </span>
      );
    }
    return (
      <TextInput
        aria-label={`Name for ${type.name}`}
        autoFocus
        value={editingName}
        onChange={(e) => setEditingName(e.target.value)}
        onBlur={() => {
          if (skipBlurSave.current) {
            skipBlurSave.current = false;
            return;
          }
          saveRename(type);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            skipBlurSave.current = true;
            saveRename(type);
          }
          if (e.key === 'Escape') {
            skipBlurSave.current = true;
            setEditingId(null);
          }
        }}
      />
    );
  };

  const renderActions = (type) => {
    if (type.archived) {
      return (
        <Button variant="link-gray" disabled={busy} aria-label={`Restore ${type.name}`} onClick={() => restore(type)}>
          Restore
        </Button>
      );
    }
    const index = activeTypes.findIndex((active) => active.id === type.id);
    return (
      <div className="d-flex justify-content-end align-items-center">
        <Button variant="tertiary" size="sm" icon={ArrowUp} aria-label={`Move ${type.name} up`} disabled={busy || index === 0} onClick={() => move(type, activeTypes[index - 1].position)} />
        <Button variant="tertiary" size="sm" icon={ArrowDown} aria-label={`Move ${type.name} down`} disabled={busy || index === activeTypes.length - 1} onClick={() => move(type, activeTypes[index + 1].position)} />
        <span className="ml-3">
          <Button variant="link-gray" disabled={busy} aria-label={`Rename ${type.name}`} onClick={() => startRename(type)}>Rename</Button>
        </span>
        <span className="ml-3">
          <Button variant="link-destructive" disabled={busy} aria-label={`Archive ${type.name}`} onClick={() => archive(type)}>Archive</Button>
        </span>
      </div>
    );
  };

  const columns = [
    { key: 'name', header: 'Type', primary: true, render: renderName },
    ...(canManage ? [{ key: 'actions', header: '', align: 'right', render: renderActions }] : []),
  ];

  return (
    <CrmPageLayout
      team={team}
      activeSidebarKey="settings"
      mainTopMargin={32}
      breadcrumb={<Breadcrumbs items={[{ label: 'Settings', href: `/teams/${team.id}/crm-settings` }, { label: 'Contact Types' }]} />}
      title="Contact Types"
      subtitle="Choose the types you can give a contact, like Major Donor or Board Member. A contact can have more than one."
    >
      <Card padding="md">
        {error && <div className="mb-3"><Alert color="error" title={error} /></div>}
        {canManage && (
          <div className="d-flex align-items-end mb-3">
            <div className="flex-grow-1 mr-3">
              <TextInput
                aria-label="New contact type"
                placeholder="e.g. Founding Circle"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  addType();
                }}
              />
            </div>
            <Button variant="primary" disabled={busy || !newName.trim()} onClick={addType}>Add type</Button>
          </div>
        )}
        <Table
          columns={columns}
          data={[...activeTypes, ...types.filter((type) => type.archived)]}
          getRowKey={(type) => type.id}
          empty={<EmptyState size="sm" title="No contact types yet." />}
        />
      </Card>
    </CrmPageLayout>
  );
}
